import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dig from 'object-dig';
import Lottie from 'react-lottie';
import CurrentUser from '~/contexts/CurrentUser';

import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import ReceiptIcon from '@material-ui/icons/Receipt';

import LinearProgress from '~/components/LinearProgress';
import Typography from '@material-ui/core/Typography';

import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import Dialog from '~/components/Dialog';
import Button from '~/components/Button';

import uploadingLottie from '~/assets/lotties/uploading-to-cloud';

import Request from '~/providers/Request';
import { URL } from '~/config/uri';
import isPresent from '~/utils/isPresent';

import Form from './Form';
import DraftStatus from './DraftStatus';
import useEvolutionDraft from './useEvolutionDraft';

function MedicalRecordEvolutionNew({
  isOpen,
  idClient,
  record,
  handleClose,
  onSuccess,
  onEmitMevo,
}) {
  const { user } = useContext(CurrentUser);

  const [evolutionText, setEvolutionText] = useState(null);
  const [files, setFiles] = useState([]);
  const [countryIdCompany, setCountryIdCompany] = useState(null);

  const [userPhysicalExamValue, setUserPhysicalExamValue] = useState('');
  const [userConductValue, setUserConductValue] = useState('');
  const [posologyValue, setPosologyValue] = useState('');
  const [pillsQuantityValue, setPillsQuantityValue] = useState('');
  const [referralLocationIdValue, setReferralLocationIdValue] = useState('');
  const [referralSpecialtyValue, setReferralSpecialtyValue] = useState('');
  const [referralNotesValue, setReferralNotesValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const { t } = useTranslation();

  const draft = useEvolutionDraft({
    isOpen,
    idClient,
    notes: dig(evolutionText, 'notes'),
    onLoad: (notes) => setEvolutionText({ notes }),
  });

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: uploadingLottie,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  async function uploadFiles(evolutionId, afterSave) {
    setLoadingFiles(true);

    const formData = new FormData();

    files.map((file) => {
      formData.append('file[]', file);
    });

    try {
      const authorizationToken = await Request.getAuth();

      const response = await fetch(
        `${URL}/admin/medical_record_evolutions/${evolutionId}/file`,
        {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `Bearer ${authorizationToken}`,
          },
        }
      );
      setLoadingFiles(false);
      if (response.ok) {
        toast.success(t('action.save_success'));
        afterSave(evolutionId);
      } else {
        toast.error(t('medical_record_evolutions.extras.save_fail'));
      }
    } catch (error) {
      console.error(error);
      setLoadingFiles(false);
    }
  }

  async function onSubmit(afterSave) {
    // afterSave pode ser a ação pós-salvar (ex.: emitir Mevo). Se vier um evento
    // (clique/submit do form), cai no default que apenas fecha + recarrega.
    const done = typeof afterSave === 'function' ? afterSave : () => onSuccess();
    const typeEvolution = user.role === 'doctor' ? 1 : 0;

    if (
      !evolutionText ||
      (evolutionText &&
        evolutionText.notes.length < 12 &&
        countryIdCompany === 1)
    ) {
      toast.error('Obrigatório inserir Evolução Médica');
      return;
    }

    if (countryIdCompany === 1 && typeEvolution === 1) {
      if (
        selectedOptions.cid.length === 0 ||
        selectedOptions.exit.length === 0
      ) {
        toast.error(
          'Obrigatório selecionar CID Principal e Saída de Atendimento'
        );
        return;
      }

      if (selectedOptions.exit.length > 1) {
        toast.error('Selecione apenas uma Saída de Atendimento');
      }
    }

    const data = {
      attributes: {
        notes: dig(evolutionText, 'notes'),
        user_id: idClient,
        medical_record_id: record.get('id'),
        date: new Date(),
        type_evolution: typeEvolution,
        physical_examination: userPhysicalExamValue,
        conduct: userConductValue,
        cid_main_id: selectedOptions.cid[0],
        cid_secondary_id: selectedOptions.secondaryCid[0],
        service_exit_id: selectedOptions.exit[0],
        exams_ids: selectedOptions.exams,
        medicines_ids: selectedOptions.medications,
        posology: posologyValue,
        pills_quantity: pillsQuantityValue ? parseInt(pillsQuantityValue, 10) : null,
        referral_location_id: referralLocationIdValue || null,
        referral_specialty: referralSpecialtyValue,
        referral_notes: referralNotesValue,
      },
    };

    // "notes": "<p>Teste insert</p>",
    // "user_id": "33663",
    // "medical_record_id": "5275",
    // "date": "2024-08-14T21:45:42.216Z",
    // "type_evolution" : 1,
    // "physical_examination": "exame físico",
    // "conduct": "conduta",
    // "cid_main_id": 1,
    // "cid_secondary_id":1,
    // "service_exit_id": 1,
    // "exams_ids": [1,2,3, 4],
    // "medicines_ids": [5,6,7]

    setLoading(true);
    await draft.settle();
    try {
      Request.post(
        '/admin/medical_record_evolutions',
        { data },
        {},
        { controller: 'medical_record_evolutions' }
      )
        .then((response) => {
          setLoading(false);
          toast.success(t('action.save_success'));
          if (isPresent(files)) {
            uploadFiles(response.data.id, done);
          } else {
            done(response.data.id);
          }
        })
        .catch((error) => {
          console.error(error);
          setLoading(false);
          draft.resume();
        });
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }

  const handleChangePhysicalExams = (e) => {
    const value = e.target.value;
    setUserPhysicalExamValue(value);
  };

  const handleChangeConduct = (e) => {
    const value = e.target.value;
    setUserConductValue(value);
  };

  const handleChangeEvolutionText = (data) => {
    setEvolutionText({ ...evolutionText, ...data });
  };

  const handleChangeMainObject = (data) => {
    setSelectedOptions(data);
  };

  const handleChangePosology = (e) => {
    setPosologyValue(e.target.value);
  };

  const handleChangePillsQuantity = (e) => {
    setPillsQuantityValue(e.target.value);
  };

  const handleChangeReferralLocationId = (e) => {
    setReferralLocationIdValue(e.target.value);
  };

  const handleChangeReferralSpecialty = (e) => {
    setReferralSpecialtyValue(e.target.value);
  };

  const handleChangeReferralNotes = (e) => {
    setReferralNotesValue(e.target.value);
  };

  useEffect(() => {
    const fetchData = async () => {
      const authorizationToken = await Request.getAuth();
      try {
        const response = await fetch(
          `${URL}/admin/users/country_id/${idClient}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${authorizationToken}`,
            },
          }
        );
        const data = await response.json();
        setCountryIdCompany(data.country_id);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    setEvolutionText(null);
    setUserPhysicalExamValue('');
    setUserConductValue('');
    setPosologyValue('');
    setPillsQuantityValue('');
    setReferralLocationIdValue('');
    setReferralSpecialtyValue('');
    setReferralNotesValue('');
    setFiles([]);
  }, [isOpen]);

  return (
    <Dialog
      icon={<LocalHospitalIcon />}
      isOpen={isOpen}
      handleClose={handleClose}
      title={t('medical_record_evolutions.extras.create_evolution')}
      dialogActions={
        <>
          <DraftStatus status={draft.status} />
          <Button
            disabled={loadingFiles || loading}
            color="grey"
            onClick={handleClose}
          >
            {t('action.cancel')}
          </Button>
          <Button
            disabled={loadingFiles || loading}
            color="green"
            onClick={onSubmit}
          >
            {t('action.save')}
          </Button>
          {countryIdCompany === 1 && user.role === 'doctor' && (
            <Button
              disabled={loadingFiles || loading}
              color="purple"
              endIcon={<ReceiptIcon />}
              onClick={() => onSubmit((id) => onEmitMevo({ id }))}
            >
              Salvar e emitir Receita (Mevo)
            </Button>
          )}
        </>
      }
    >
      <div onBlur={draft.flush}>
        {!loadingFiles && (
          <Form
            countryIdCompany={countryIdCompany}
            onSubmit={onSubmit}
            setFiles={setFiles}
            loading={loading}
            evolution={evolutionText}
            userPhysicalExamValue={userPhysicalExamValue}
            userConductValue={userConductValue}
            posologyValue={posologyValue}
            pillsQuantityValue={pillsQuantityValue}
            referralLocationIdValue={referralLocationIdValue}
            referralSpecialtyValue={referralSpecialtyValue}
            referralNotesValue={referralNotesValue}
            handleChangeEvolutionText={handleChangeEvolutionText}
            handleChangePhysicalExams={handleChangePhysicalExams}
            handleChangeConduct={handleChangeConduct}
            handleChangeMainObject={handleChangeMainObject}
            handleChangePosology={handleChangePosology}
            handleChangePillsQuantity={handleChangePillsQuantity}
            handleChangeReferralLocationId={handleChangeReferralLocationId}
            handleChangeReferralSpecialty={handleChangeReferralSpecialty}
            handleChangeReferralNotes={handleChangeReferralNotes}
          />
        )}
        <LinearProgress loading={loadingFiles} />
        {loadingFiles && (
          <GridContainer direction="row" justify="center" alignItems="center">
            <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
              <Lottie options={defaultOptions} />
            </GridItem>
            <Typography
              variant="body2"
              color="secondary"
              style={{
                textAlign: 'center',
              }}
            >
              Aguarde enquanto salvamos os anexos.
            </Typography>
          </GridContainer>
        )}
        <LinearProgress loading={loadingFiles} />
      </div>
    </Dialog>
  );
}

MedicalRecordEvolutionNew.propTypes = {
  isOpen: PropTypes.bool.isRequired,
};

MedicalRecordEvolutionNew.defaultProps = {
  isOpen: false,
  onEmitMevo: () => {},
};

export default MedicalRecordEvolutionNew;
